"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type CoursePart,
  courseLectureIsReady,
  ONLINE_COURSE_FIELDS as F,
  formatOnlineCourse,
  type OnlineCourseLabels,
  wholeCourseIsReady,
} from "@/lib/tool-kit/media-sources/online-course-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const ONLINE_COURSE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Course details",
  resultsHeading: "Your citations",
  emptyPromptCourse: "Enter the course title to see your citations.",
  emptyPromptLecture:
    "Enter the title of the lecture or module and the course it belongs to.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  partLabel: "What are you citing?",
  partCourse: "The whole course",
  partLecture: "One lecture, video or module",
  lecturersLabel: "Lecturer(s) of this part",
  lecturersPlaceholder: "Jason Tangen",
  lecturersHint: "The person who gives this lecture. One per line.",
  lectureTitleLabel: "Title of the lecture, video or module",
  lectureTitlePlaceholder:
    "Episode 2: I heard a tapping somewhat louder than before",
  instructorsLabel: "Course instructor(s)",
  instructorsPlaceholder: "Matthew O. Jackson",
  instructorsHint:
    "One per line, in the order the course lists them. Name the instructors only if they created the course.",
  titleLabel: "Course title",
  titlePlaceholder: "Game theory",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  kindLabel: "Kind of course",
  kindMooc: "MOOC (Coursera, edX, FutureLearn…)",
  kindCourse: "Other online course (LinkedIn Learning, Khan Academy…)",
  platformLabel: "Platform",
  platformPlaceholder: "Coursera",
  platformHint:
    "The site that hosts the course. For your school's learning management system, write it as APA does, such as Canvas@Your University.",
  institutionLabel: "Institution offering it (optional)",
  institutionPlaceholder:
    "Stanford University and the University of British Columbia",
  dateLabel: "Year or date (if shown)",
  datePlaceholder: "2016",
  dateHint:
    "Leave blank if the course page gives no date; APA then writes n.d.",
  accessedLabel: "Date you accessed it",
  accessedPlaceholder: "YYYY-MM-DD",
  accessedHint:
    "MLA and Chicago add it when the course is undated, because course pages change.",
  urlLabel: "URL",
  urlPlaceholder: "https://www.coursera.org/learn/game-theory-1",
  urlHint:
    "The course's main page if lectures sit behind a login. For a learning management system, the login page.",
  restrictedLabel:
    "Only enrolled students can open it, and my readers are not enrolled",
  restrictedHint:
    "For class materials behind your school's login, written for readers outside the class.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 labels a massive open online course [MOOC] and a lecture from one [MOOC lecture], with the course faculty in the In position. Material your readers can open through a class login gets the site's name and login URL.",
  apaRestrictedNote:
    "APA 7 cites course material your readers cannot open as a personal communication (§8.9): in the text only, with the exact date, and no reference list entry.",
  mlaNote:
    "MLA 9 sets a course title in plain type, capitalized like any title. The platform is a container in italics, and an undated course gets the date you accessed it.",
  chicagoNote:
    "The Chicago Manual has no MOOC example. This treats the course as web content, as its Quick Guide does: the title in quotation marks, the site, and an access date when the page is undated.",
  chicagoRestrictedNote:
    "Chicago cites material readers cannot open in a note only, like a personal communication, and leaves it out of the bibliography.",
  exampleCourse: {
    instructors: "Matthew O. Jackson\nKevin Leyton-Brown\nYoav Shoham",
    title: "Game theory",
    kind: "mooc",
    platform: "Coursera",
    institution: "Stanford University and the University of British Columbia",
    accessed: "2026-10-05",
    url: "https://www.coursera.org/learn/game-theory-1",
  },
  exampleLecture: {
    lecturers: "Jason Tangen",
    lectureTitle: "Episode 2: I heard a tapping somewhat louder than before",
    instructors: "Emma MacKenzie\nJason Tangen\nMatthew Thompson",
    title: "The science of everyday thinking",
    kind: "mooc",
    platform: "edX",
    institution: "University of Queensland",
    date: "2016",
  },
} as const;

type Strings = typeof ONLINE_COURSE_CITATION_GENERATOR_STRINGS;

function lectureOnlyFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.lecturers,
      kind: "people",
      label: strings.lecturersLabel,
      placeholder: strings.lecturersPlaceholder,
      hint: strings.lecturersHint,
    },
    {
      id: F.lectureTitle,
      kind: "text",
      label: strings.lectureTitleLabel,
      placeholder: strings.lectureTitlePlaceholder,
    },
  ];
}

function courseFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.instructors,
      kind: "people",
      label: strings.instructorsLabel,
      placeholder: strings.instructorsPlaceholder,
      hint: strings.instructorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.kind,
      kind: "select",
      label: strings.kindLabel,
      options: [
        { value: "mooc", label: strings.kindMooc },
        { value: "course", label: strings.kindCourse },
      ],
    },
    {
      id: F.platform,
      kind: "text",
      label: strings.platformLabel,
      placeholder: strings.platformPlaceholder,
      hint: strings.platformHint,
    },
    {
      id: F.institution,
      kind: "text",
      label: strings.institutionLabel,
      placeholder: strings.institutionPlaceholder,
      wide: true,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.dateHint,
    },
    {
      id: F.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.accessedPlaceholder,
      hint: strings.accessedHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
      wide: true,
    },
    {
      id: F.restricted,
      kind: "checkbox",
      label: strings.restrictedLabel,
      hint: strings.restrictedHint,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function onlineCourseLabels(strings: Strings): OnlineCourseLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    apaRestrictedNote: strings.apaRestrictedNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    chicagoRestrictedNote: strings.chicagoRestrictedNote,
  };
}

export default function OnlineCourseCitationGeneratorWidget() {
  const strings = useToolUi(ONLINE_COURSE_CITATION_GENERATOR_STRINGS);
  const [part, setPart] = useState<CoursePart>("course");
  const fields = useMemo(
    () =>
      part === "lecture"
        ? [...lectureOnlyFields(strings), ...courseFields(strings)]
        : courseFields(strings),
    [part, strings]
  );
  const labels = useMemo(() => onlineCourseLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatOnlineCourse(part, values, labels),
    [part, labels]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt:
        part === "lecture"
          ? strings.emptyPromptLecture
          : strings.emptyPromptCourse,
    }),
    [part, strings]
  );

  return (
    <SourceCitationBuilder
      example={
        part === "lecture" ? strings.exampleLecture : strings.exampleCourse
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.partLabel}
          name="online-course-part"
          onChange={setPart}
          options={[
            { value: "course", label: strings.partCourse },
            { value: "lecture", label: strings.partLecture },
          ]}
          value={part}
        />
      }
      isReady={part === "lecture" ? courseLectureIsReady : wholeCourseIsReady}
      strings={builderStrings}
    />
  );
}
